import type { FormEvent } from 'react';
import { useCallback, useMemo } from 'react';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useCreateIssueDraft } from '#src/app/pages/homepage/shared/hooks';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

type SapPromptHookResult = ModelHookResult<
	{
		isSubmitting: boolean;
	},
	{
		onSubmit: (event: FormEvent<HTMLFormElement>) => void;
	}
>;

export function useSapIssueForm(): SapPromptHookResult {
	const track = useTracking();
	const navigate = useNavigate();

	const createDraftMutation = useCreateIssueDraft({ sourceLocation: 'homepage_primary_button' });

	const isSubmitting = useMemo(() => {
		if (isLoading(createDraftMutation)) return true;
		if (isLoaded(createDraftMutation)) return true;
		return false;
	}, [createDraftMutation]);

	const handleSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const formData = new FormData(event.target as HTMLFormElement);
			const input = (formData.get('issue') as string) || '';
			const trimmed = input.trim();
			void track('homepage_SAP_click', {
				cta: 'primary',
				prompt: trimmed,
			});

			if (isSubmitting) return;

			if (!trimmed.length) {
				navigate('/start-a-petition/intro?source_location=homepage_primary_button');
				return;
			}

			createDraftMutation.mutate(trimmed);
		},
		[track, isSubmitting, createDraftMutation, navigate],
	);

	return {
		data: {
			isSubmitting,
		},
		actions: {
			onSubmit: handleSubmit,
		},
	};
}
